import {
  ElementType,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  Ref,
} from 'react';

import { Dialog, type DialogTriggerProps } from '@/components/atoms';
import { cn } from '@/lib/utils/tailwind';

import { AsElement } from '@/types';

type SheetTriggerProps<T extends ElementType = 'button'> =
  DialogTriggerProps<T>;

/*
required to create an inner & outer component due to radix needing forwardRef
and the fact we use generics
 */
const SheetTriggerBase = forwardRef<
  HTMLElement,
  {
    as?: ElementType;
    className?: string;
    children?: ReactNode;
  } & Omit<HTMLAttributes<HTMLElement>, 'ref' | 'className' | 'children'>
>(({ as, className, children, ...props }, ref) => {
  const Component: ElementType = as || 'button';

  return (
    <Dialog.Trigger
      as={Component}
      className={cn([], className)}
      ref={ref}
      {...props}
    >
      {children}
    </Dialog.Trigger>
  );
});
SheetTriggerBase.displayName = 'SheetTrigger';

const SheetTrigger = (<T extends ElementType = 'button'>(
  props: SheetTriggerProps<T> & {
    ref?: Ref<AsElement<T>>;
  },
): ReactElement => {
  return <SheetTriggerBase {...props} />;
}) as <T extends ElementType = 'button'>(
  props: SheetTriggerProps<T> & {
    ref?: Ref<AsElement<T>>;
  },
) => ReactElement;

Object.defineProperty(SheetTrigger, 'displayName', {
  value: 'SheetTrigger',
  configurable: true,
});

export { SheetTrigger, type SheetTriggerProps };
